/**
 * Alarm field catalog hooks — drives the catalog-driven condition builder.
 * Catalog is relatively static (only changes when new watchpoint types ship),
 * so staleTime is set high (10 minutes).
 */
import { useQuery } from '@tanstack/react-query';
import {
  healthcheckApi,
  hcKeys,
  unwrap,
  unwrapList,
  type WatchpointFieldCatalog,
  type WatchpointType,
} from './_shared';

/** All catalogs (`GET /alarm-fields`) — use for populating a type picker. */
export function useAlarmFieldCatalog() {
  return useQuery<WatchpointFieldCatalog[]>({
    queryKey: hcKeys.alarmCatalog(),
    staleTime: 10 * 60 * 1000,
    queryFn: async () =>
      unwrapList<WatchpointFieldCatalog>(await healthcheckApi.get('/alarm-fields')).items,
  });
}

/** Single-type catalog (`GET /alarm-fields/:type`) — fetch after watchpoint is chosen. */
export function useAlarmFieldCatalogByType(type: WatchpointType | undefined) {
  return useQuery<WatchpointFieldCatalog>({
    queryKey: hcKeys.alarmCatalog(type),
    enabled: !!type,
    staleTime: 10 * 60 * 1000,
    queryFn: async () =>
      unwrap<WatchpointFieldCatalog>(await healthcheckApi.get(`/alarm-fields/${type}`)),
  });
}

export type { WatchpointFieldCatalog, CatalogField, FieldKind, EvalLevel, MetricInfo, MetricGroup } from './_shared';
